import React, { useEffect, useMemo, useState } from "react";
import { Option, Select } from "@mui/joy";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { getFetch, postFetch } from "../../../../../fetch/fetch";

// 데이터 맞추기 : 새벽 배치(paymentReconcile)가 감지한 결제 불일치를 관리자가 확인/처리하는 화면
// - sourceCanceled : 서버는 결제완료인데 실제(결제선생/단말기)는 취소된 건
// - serverOnlyCanceled : 서버는 취소인데 실제는 결제 유지중인 건
// - priceMismatch : 서버 금액과 실제 승인 금액이 다른 건

const PaymentReconcile: React.FC<any> = (props) => {

    // pending(미처리) / history(처리 기록) 뷰 전환
    const [view, setView] = useState<string>("pending");

    const [rows, setRows] = useState<any[]>([]);
    const [historyRows, setHistoryRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [savingId, setSavingId] = useState<number | null>(null);

    // 카드별 메모 입력값 (queueId 기준)
    const [memoMap, setMemoMap] = useState<{ [key: number]: string }>({});

    // 클라이언트 필터
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [locationFilter, setLocationFilter] = useState<string>("all");

    const mismatchTypeToKorean = (mismatchType: string) => {
        if (mismatchType === "sourceCanceled") return "취소 불일치";
        if (mismatchType === "serverOnlyCanceled") return "역방향 취소 불일치";
        if (mismatchType === "priceMismatch") return "금액 불일치";
        return mismatchType;
    };

    const sourceTypeToKorean = (sourceType: string) => {
        if (sourceType === "payTeacher") return "결제선생";
        if (sourceType === "terminal") return "단말기";
        return sourceType;
    };

    const sourceStateToKorean = (sourceState: string) => {
        if (sourceState === "F") return "결제완료";
        if (sourceState === "W") return "미결제";
        if (sourceState === "C") return "결제취소";
        if (sourceState === "D") return "파기";
        if (sourceState === "USN") return "승인 유지중";
        if (sourceState === "UVN") return "취소됨";
        return sourceState;
    };

    const statusToKorean = (status: string) => {
        if (status === "applied") return "맞춤 처리";
        if (status === "skipped") return "넘어감";
        if (status === "autoResolved") return "자동 해소";
        return status;
    };

    const statusToColor = (status: string) => {
        if (status === "applied") return "#2e7d32";
        if (status === "skipped") return "#9e9e9e";
        if (status === "autoResolved") return "#1565c0";
        return "#333";
    };

    const formatKST = (value: string) => {
        if (!value) return "";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return value;

        // 한국시간으로 변환
        const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);

        const yyyy = kst.getFullYear();
        const mm = ("0" + (kst.getMonth() + 1)).slice(-2);
        const dd = ("0" + kst.getDate()).slice(-2);
        const hh = ("0" + kst.getHours()).slice(-2);
        const mi = ("0" + kst.getMinutes()).slice(-2);

        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    };

    // 한국시간 날짜만 (수강기간 표시용)
    const formatKSTDate = (value: string) => {
        if (!value) return "-";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return value;

        const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);

        const yyyy = kst.getFullYear();
        const mm = ("0" + (kst.getMonth() + 1)).slice(-2);
        const dd = ("0" + kst.getDate()).slice(-2);

        return `${yyyy}-${mm}-${dd}`;
    };

    // sourceSnapshot(JSON)이 문자열로 올 수도 있어 안전하게 파싱
    const getSourceSnapshot = (row: any) => {
        if (!row.sourceSnapshot) return null;
        if (typeof row.sourceSnapshot === "object") return row.sourceSnapshot;
        try {
            return JSON.parse(row.sourceSnapshot);
        } catch (e) {
            return null;
        }
    };

    // 결제선생 일시 형식(YYYYMMDDHHmmss) → 보기 좋게
    const formatApprDt = (apprDt: string) => {
        if (!apprDt || apprDt.length < 12) return apprDt || "";
        return `${apprDt.slice(0, 4)}-${apprDt.slice(4, 6)}-${apprDt.slice(6, 8)} ${apprDt.slice(8, 10)}:${apprDt.slice(10, 12)}`;
    };

    // 취소가 일어난 일시 (취소 불일치: 결제선생/단말기 쪽 취소일, 역방향: 웹페이지 취소확정일)
    const cancelDateText = (row: any) => {
        if (row.mismatchType === "sourceCanceled") {
            const snapshot = getSourceSnapshot(row);
            let dateText = "";

            if (snapshot) {
                // 결제선생: 취소 건의 appr_dt가 취소일시 (appr_origin_dt가 원결제일시)
                if (row.sourceType === "payTeacher") dateText = formatApprDt(snapshot.appr_dt);
                // 단말기: 취소(UVN) 거래의 거래일시
                if (row.sourceType === "terminal") dateText = snapshot.transAt || "";
            }

            return dateText ? `${sourceTypeToKorean(row.sourceType)} 취소일시: ${dateText}` : "";
        }

        if (row.mismatchType === "serverOnlyCanceled" && row.serverCanceledAt) {
            return `웹페이지 취소확정일시: ${formatKST(row.serverCanceledAt)}`;
        }

        return "";
    };

    // 유형별 웹페이지 vs 결제선생/단말기 비교 문구 (행정팀용 용어)
    const mismatchDetailText = (row: any) => {
        const sourceLabel = sourceTypeToKorean(row.sourceType);

        if (row.mismatchType === "sourceCanceled") {
            return `웹페이지: 정상결제 ${Number(row.serverPrice).toLocaleString()}원 / ${sourceLabel}: 취소됨(${sourceStateToKorean(row.sourceState)})`;
        }
        if (row.mismatchType === "serverOnlyCanceled") {
            return `웹페이지: 취소됨 / ${sourceLabel}: 결제 유지중(${Number(row.sourcePrice || row.serverPrice).toLocaleString()}원)`;
        }
        if (row.mismatchType === "priceMismatch") {
            return `웹페이지 금액 ${Number(row.serverPrice).toLocaleString()}원 ≠ ${sourceLabel} 금액 ${Number(row.sourcePrice).toLocaleString()}원`;
        }
        return "";
    };

    // 유형별 맞추기 확인 문구
    const applyConfirmMessage = (row: any) => {
        const sourceLabel = sourceTypeToKorean(row.sourceType);

        if (row.mismatchType === "sourceCanceled") {
            return `${sourceLabel}(취소됨)에 맞춰 웹페이지 결제기록을 취소 처리합니다.\n남은 결제액이 0원이면 수강권도 자동 취소됩니다.\n진행할까요?`;
        }
        if (row.mismatchType === "serverOnlyCanceled") {
            return `${sourceLabel}(결제 유지중)에 맞춰 웹페이지 기록의 취소 표시를 해제합니다.\n진행할까요?`;
        }
        if (row.mismatchType === "priceMismatch") {
            return `웹페이지 금액을 ${sourceLabel} 승인 금액 ${Number(row.sourcePrice).toLocaleString()}원으로 수정합니다.\n진행할까요?`;
        }
        return "진행할까요?";
    };

    const getReconcileList = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch("/dashboard/payments/managepayment/reconcilelist");
            const data = result?.data || [];
            setRows(data);

            // 서버에 저장된 메모로 입력값 초기화
            const newMemoMap: { [key: number]: string } = {};
            data.forEach((row: any) => {
                if (row.memo) newMemoMap[row.queueId] = row.memo;
            });
            setMemoMap(newMemoMap);
        } catch (e) {
            console.log(e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const getHistoryList = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch("/dashboard/payments/managepayment/reconcilelist?mode=history");
            setHistoryRows(result?.data || []);
        } catch (e) {
            console.log(e);
            setHistoryRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getReconcileList();
    }, []);

    useEffect(() => {
        if (view === "history") {
            getHistoryList();
        }
    }, [view]);

    // 새로고침 : 현재 리스트만 지금 시각 기준으로 실제 데이터와 재확인
    const goRefresh = async () => {
        if (!window.confirm("리스트의 결제 건들을 지금 시각 기준으로 결제선생/단말기 데이터와 다시 확인할까요?\n(조회에 수십 초 걸릴 수 있습니다)")) return;

        try {
            setRefreshing(true);
            const result: any = await postFetch("/dashboard/payments/managepayment/refreshreconcile", {});

            if (result.message === "success") {
                toast.success(
                    `재확인 완료 — ${result.autoResolvedCount}건 자동 해소, ${result.stillMismatchCount}건 여전히 불일치`
                    + (result.failedCount ? `, ${result.failedCount}건 확인 실패` : "")
                );
                await getReconcileList();
            } else if (result.message === "alreadyRunning") {
                toast.error("정합성 점검 작업이 이미 실행 중입니다. 잠시 후 다시 시도해주세요.");
            } else {
                toast.error("재확인 실패: " + result.message);
            }
        } catch (e) {
            console.log(e);
            toast.error("재확인 중 오류가 발생했습니다.");
        } finally {
            setRefreshing(false);
        }
    };

    // 메모 저장 (처리 전에도 남겨둘 수 있게)
    const goSaveMemo = async (row: any) => {
        try {
            setSavingId(row.queueId);
            const result: any = await postFetch("/dashboard/payments/managepayment/reconcilememo", {
                queueId: row.queueId,
                memo: memoMap[row.queueId] || ""
            });

            if (result.message === "success") {
                toast.success("메모가 저장되었습니다.");
            } else {
                toast.error("메모 저장 실패: " + result.message);
            }
        } catch (e) {
            console.log(e);
            toast.error("메모 저장 중 오류가 발생했습니다.");
        } finally {
            setSavingId(null);
        }
    };

    // 실제랑 맞추기
    const goApply = async (row: any) => {
        if (!window.confirm(applyConfirmMessage(row))) return;

        try {
            setSavingId(row.queueId);
            const result: any = await postFetch("/dashboard/payments/managepayment/applyreconcile", { queueId: row.queueId, memo: (memoMap[row.queueId] || "").trim() });

            if (result.message === "success") {
                toast.success(`${sourceTypeToKorean(row.sourceType)} 데이터에 맞게 수정되었습니다.` + (result.orderItemCanceled ? " (남은 결제액 0원이라 수강권도 취소됨)" : ""));

                if (result.warning === "orderItemStillCanceled") {
                    toast.warn("이 결제의 수강권은 이미 취소된 상태입니다. 수강권 상태를 수동으로 확인해주세요.");
                }

                await getReconcileList();
            } else {
                toast.error("처리 실패: " + result.message);
            }
        } catch (e) {
            console.log(e);
            toast.error("처리 중 오류가 발생했습니다.");
        } finally {
            setSavingId(null);
        }
    };

    // 넘어가기 (메모 필수)
    const goSkip = async (row: any) => {
        const memo = (memoMap[row.queueId] || "").trim();

        if (!memo) {
            toast.warn("넘어가기 하려면 어떤 이유인지 메모를 먼저 적어주세요.");
            return;
        }

        if (!window.confirm("이 불일치를 무시 처리할까요?\n리스트에서 사라지고 다시 표시되지 않습니다. (처리 기록에서는 계속 볼 수 있습니다)")) return;

        try {
            setSavingId(row.queueId);
            const result: any = await postFetch("/dashboard/payments/managepayment/skipreconcile", { queueId: row.queueId, memo });

            if (result.message === "success") {
                toast.success("넘어가기 처리되었습니다.");
                await getReconcileList();
            } else if (result.message === "noMemo") {
                toast.warn("넘어가기 하려면 메모를 먼저 적어주세요.");
            } else {
                toast.error("처리 실패: " + result.message);
            }
        } catch (e) {
            console.log(e);
            toast.error("처리 중 오류가 발생했습니다.");
        } finally {
            setSavingId(null);
        }
    };

    const locationOptions = useMemo(() => {
        const locationSet = new Set<string>();
        rows.forEach((row) => {
            if (row.location) locationSet.add(row.location);
        });
        return Array.from(locationSet);
    }, [rows]);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            if (typeFilter !== "all" && row.mismatchType !== typeFilter) return false;
            if (locationFilter !== "all" && (row.location || "") !== locationFilter) return false;
            return true;
        });
    }, [rows, typeFilter, locationFilter]);

    return (
        <div style={{ width: "1100px", margin: "24px auto 60px auto", fontFamily: "Apple_R" }}>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                <div style={{ fontSize: "20px", fontFamily: "Apple_SB" }}>
                    데이터 맞추기 {view === "pending" ? `(${filteredRows.length}건)` : ""}
                </div>
                <ToggleButtonGroup
                    color="primary"
                    value={view}
                    exclusive
                    size="small"
                    onChange={(e, newValue) => { if (newValue) setView(newValue); }}
                >
                    <ToggleButton value="pending">미처리</ToggleButton>
                    <ToggleButton value="history">처리 기록</ToggleButton>
                </ToggleButtonGroup>
            </div>

            <div style={{ fontSize: "13px", color: "#666", marginBottom: "20px" }}>
                매일 새벽 4:30에 최근 5개월치 웹페이지 결제기록을 결제선생/단말기 실데이터와 비교해 불일치를 찾습니다.
            </div>

            {
                view === "pending" ?
                    <div>
                        <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
                            <ToggleButtonGroup
                                color="primary"
                                value={typeFilter}
                                exclusive
                                size="small"
                                onChange={(e, newValue) => { if (newValue) setTypeFilter(newValue); }}
                            >
                                <ToggleButton value="all">전체</ToggleButton>
                                <ToggleButton value="sourceCanceled">취소 불일치</ToggleButton>
                                <ToggleButton value="serverOnlyCanceled">역방향</ToggleButton>
                                <ToggleButton value="priceMismatch">금액 불일치</ToggleButton>
                            </ToggleButtonGroup>

                            <Select
                                value={locationFilter}
                                onChange={(e, newValue) => setLocationFilter(newValue as string)}
                                placeholder="지점 선택"
                                style={{ width: "160px" }}
                            >
                                <Option value="all">전체 지점</Option>
                                {locationOptions.map((location) => (
                                    <Option key={location} value={location}>
                                        {location}
                                    </Option>
                                ))}
                            </Select>

                            <div style={{ marginLeft: "auto" }}>
                                <Button
                                    variant="outlined"
                                    disabled={refreshing || loading}
                                    onClick={goRefresh}
                                    startIcon={refreshing ? <CircularProgress size={16} /> : undefined}
                                >
                                    {refreshing ? "결제선생/단말기 확인 중..." : "새로고침 (실시간 재확인)"}
                                </Button>
                            </div>
                        </div>

                        {
                            loading &&
                            <div style={{ padding: "40px 0", textAlign: "center" }}>
                                <CircularProgress />
                            </div>
                        }

                        {
                            !loading && filteredRows.length === 0 &&
                            <div style={{ padding: "40px 0", textAlign: "center", color: "#888" }}>
                                현재 불일치 건이 없습니다.
                            </div>
                        }

                        {
                            !loading && filteredRows.map((row: any) => (
                                <div
                                    key={row.queueId}
                                    style={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                        padding: "16px",
                                        marginBottom: "16px",
                                        backgroundColor: "white"
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <div style={{ fontFamily: "Apple_SB", fontSize: "15px" }}>
                                            {row.studentName || "학생 정보 없음"} <span style={{ color: "#999", fontFamily: "Apple_R", fontSize: "12px" }}>(결제기록 #{row.paymentRecordId})</span>
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#d32f2f", fontFamily: "Apple_SB" }}>
                                            {sourceTypeToKorean(row.sourceType)} · {mismatchTypeToKorean(row.mismatchType)}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: "13px", color: "#555", marginTop: "6px" }}>
                                        지점: {row.location || "-"} &nbsp;|&nbsp; 결제일: {formatKST(row.paidAt)} &nbsp;|&nbsp; 수강기간: {formatKSTDate(row.startDate)} ~ {formatKSTDate(row.endDate)}
                                    </div>

                                    <div style={{ marginTop: "12px", background: "#fafafa", borderRadius: "6px", padding: "10px 12px" }}>
                                        <div style={{ fontSize: "14px", color: "#333", fontFamily: "Apple_SB" }}>
                                            {mismatchDetailText(row)}
                                        </div>
                                        {
                                            cancelDateText(row) &&
                                            <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                                                {cancelDateText(row)}
                                            </div>
                                        }
                                        <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                                            최초 감지: {formatKST(row.detectedAt)} &nbsp;|&nbsp; 마지막 확인: {formatKST(row.lastSeenAt)}
                                        </div>
                                        {
                                            row.mismatchType === "sourceCanceled" && row.orderItemToBeCanceled === 1 && row.orderItemCanceled !== 1 &&
                                            <div style={{ fontSize: "12px", color: "#1565c0", marginTop: "4px", fontFamily: "Apple_SB" }}>
                                                ℹ 수강권이 취소예정 상태입니다 — {sourceTypeToKorean(row.sourceType)}에서 취소된 게 확인됐고 취소확정만 안 누른 케이스로 보입니다. 맞추기 시 취소확정과 동일하게 처리됩니다.
                                            </div>
                                        }
                                        {
                                            row.mismatchType === "serverOnlyCanceled" && row.orderItemCanceled === 1 &&
                                            <div style={{ fontSize: "12px", color: "#e65100", marginTop: "4px", fontFamily: "Apple_SB" }}>
                                                ⚠ 수강권은 이미 취소된 상태입니다 — 맞추기 후 수강권 상태 수동 확인 필요
                                            </div>
                                        }
                                    </div>

                                    <div style={{ display: "flex", gap: "10px", marginTop: "12px", alignItems: "flex-start" }}>
                                        <TextField
                                            size="small"
                                            multiline
                                            maxRows={3}
                                            placeholder="메모 (넘어가기 시 필수)"
                                            value={memoMap[row.queueId] || ""}
                                            onChange={(e) => setMemoMap((prev) => ({ ...prev, [row.queueId]: e.target.value }))}
                                            style={{ flex: 1 }}
                                            inputProps={{ style: { fontSize: "13px", fontFamily: "Apple_R" } }}
                                        />
                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            size="small"
                                            disabled={savingId === row.queueId || refreshing}
                                            onClick={() => goSaveMemo(row)}
                                            style={{ flexShrink: 0, whiteSpace: "nowrap", marginTop: "3px" }}
                                        >
                                            메모 저장
                                        </Button>
                                    </div>

                                    <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={savingId === row.queueId || refreshing}
                                            onClick={() => goApply(row)}
                                        >
                                            {sourceTypeToKorean(row.sourceType)}에 맞추기
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            size="small"
                                            disabled={savingId === row.queueId || refreshing}
                                            onClick={() => goSkip(row)}
                                        >
                                            넘어가기
                                        </Button>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                    :
                    <div>
                        {
                            loading &&
                            <div style={{ padding: "40px 0", textAlign: "center" }}>
                                <CircularProgress />
                            </div>
                        }

                        {
                            !loading && historyRows.length === 0 &&
                            <div style={{ padding: "40px 0", textAlign: "center", color: "#888" }}>
                                처리 기록이 없습니다.
                            </div>
                        }

                        {
                            !loading && historyRows.map((row: any) => (
                                <div
                                    key={row.queueId}
                                    style={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                        padding: "12px 16px",
                                        marginBottom: "10px",
                                        backgroundColor: "white",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: "12px"
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: "14px" }}>
                                            <span style={{ fontFamily: "Apple_SB" }}>{row.studentName || "학생 정보 없음"}</span>
                                            <span style={{ color: "#999", fontSize: "12px" }}> (결제기록 #{row.paymentRecordId})</span>
                                            <span style={{ color: "#555", fontSize: "13px" }}> &nbsp; {sourceTypeToKorean(row.sourceType)} · {mismatchTypeToKorean(row.mismatchType)}</span>
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                                            {mismatchDetailText(row)}
                                        </div>
                                        {
                                            cancelDateText(row) &&
                                            <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                                                {cancelDateText(row)}
                                            </div>
                                        }
                                        <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                                            문제 감지: {formatKST(row.detectedAt)} &nbsp;→&nbsp; 처리: {formatKST(row.resolvedAt)}
                                        </div>
                                        {
                                            row.memo &&
                                            <div style={{ fontSize: "12px", color: "#555", marginTop: "2px" }}>
                                                메모: {row.memo}
                                            </div>
                                        }
                                    </div>
                                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                                        <div style={{ fontSize: "13px", fontFamily: "Apple_SB", color: statusToColor(row.status) }}>
                                            {statusToKorean(row.status)}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                                            처리자: {row.status === "autoResolved" ? "자동 해소" : (row.resolvedByName || "-")}
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
            }
        </div>
    );
};

export default PaymentReconcile;
